import { useEffect, useState } from "react";
import { Globe2, Layers, Loader2, MapPin, ShieldCheck, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../../connect/api";

const PERM_ROWS = [
  ["dept_visibility", "Department visibility", "Which department groups they can open", [["own", "Own department only"], ["all", "All departments"]]],
  ["directory", "Staff directory", "Who appears in their directory", [["full", "Full directory"], ["department", "Own department only"]]],
  ["group_visibility", "Group chats", "Groups they can see", [["all", "All their groups"], ["own_dept", "Department group only"]]],
];
const TOGGLES = [
  ["inter_dept", "Inter-department messaging", "Can message staff outside their own department (off = only via your grants)"],
  ["broadcasts", "All-staff broadcasts", "Receives and sees your broadcasts sent to all groups"],
  ["direct_messages", "Start direct messages", "Off = they can only reply to chats others start"],
  ["attachments", "File & photo sharing", "Can send attachments in chats"],
  ["cross_office", "Other office directory", "May open the other office (Infopark ↔ Kathrikadavu) in the Directory"],
];
const CHANNELS = [["both", "Both"], ["web", "Web only"], ["mobile", "Mobile only"]];

const Seg = ({ value, options, onChange, testid }) => (
  <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }} data-testid={testid}>
    {options.map(([v, l]) => <button type="button" key={v} onClick={() => onChange(v)} className={`rounded-lg py-1.5 px-1 text-[11px] font-semibold border ${value === v ? "border-amber-400/60 bg-amber-500/10 text-amber-300" : "border-white/10 text-slate-400 hover:text-slate-200"}`} data-testid={`${testid}-${v}`}>{l}</button>)}
  </div>
);

const Toggle = ({ on, onChange, testid }) => (
  <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${on ? "bg-emerald-500" : "bg-white/15"}`} data-testid={testid}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-5.5 left-0.5" : "left-0.5"}`} style={{ transform: on ? "translateX(20px)" : "none" }} />
  </button>
);

/** CEO: per-staff permissions + login access. Every change saves immediately and applies on the member's next request. */
export const StaffPerms = ({ staff, offices, onClose, onSaved }) => {
  const [perms, setPerms] = useState(staff.perms);
  const [access, setAccess] = useState(staff.access);
  const [busy, setBusy] = useState("");
  const [presets, setPresets] = useState([]);
  useEffect(() => { api.get("/admin/perm-presets").then((r) => setPresets(r.data.items)).catch(() => {}); }, []);
  const save = async (field, patch) => {
    setBusy(field);
    try {
      const { data } = await api.put(`/admin/staff/${staff.id}/perms`, patch);
      setPerms(data.perms); setAccess(data.access); onSaved(data);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const setP = (k, v) => { setPerms({ ...perms, [k]: v }); save(k, { perms: { [k]: v } }); };
  const setA = (k, v) => { setAccess({ ...access, [k]: v }); save(k, { access: { [k]: v } }); };
  const saveAsPreset = async () => {
    const name = window.prompt("Preset name (e.g. Field staff, Intern):", "");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/admin/perm-presets", { name: name.trim(), perms, access });
      setPresets((p) => [...p.filter((x) => x.id !== data.id), data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`Preset "${data.name}" saved`);
    } catch (e) { toast.error(errText(e)); }
  };
  const applyPreset = async (pid) => {
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    setPerms(p.perms); setAccess(p.access);
    await save("preset", { perms: p.perms, access: p.access });
    toast.success(`"${p.name}" applied to ${staff.name}`);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0e1626] border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto nxc-fade-in" data-testid="staff-perms-sheet">
        <div className="sticky top-0 bg-[#0e1626] px-4 pt-4 pb-3 border-b border-white/10 flex items-center gap-3">
          <ShieldCheck size={18} className="text-amber-300" />
          <div className="flex-1 min-w-0"><h2 className="nxc-display text-base font-semibold truncate">{staff.name}</h2><div className="text-[11px] text-slate-400 truncate">Permissions · {staff.department} · {(staff.office || "").replace("NST | ", "")}</div></div>
          {busy && <Loader2 size={14} className="animate-spin text-amber-300" />}
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="staff-perms-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-5">
          <div className="flex items-center gap-2" data-testid="staff-perms-presets-row">
            <select className="nxc-input flex-1 h-9 text-sm" value="" onChange={(e) => e.target.value && applyPreset(e.target.value)} data-testid="staff-perms-apply-preset">
              <option value="">{presets.length ? "Apply a preset…" : "No presets saved yet"}</option>
              {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button type="button" onClick={saveAsPreset} className="nxc-btn nxc-btn-ghost h-9 text-xs shrink-0" data-testid="staff-perms-save-preset"><Layers size={13} /> Save as preset</button>
          </div>
          <section className="space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><Smartphone size={12} /> Login access</div>
            <div className="nxc-card p-3 space-y-3">
              <div><div className="text-sm font-semibold text-slate-100">Sign-in channel</div><p className="text-[11px] text-slate-400 mb-2">Where this person may sign in. Blocked attempts look like a wrong password.</p><Seg value={access.channel} options={CHANNELS} onChange={(v) => setA("channel", v)} testid="perm-channel" /></div>
              {access.channel !== "mobile" && (
                <div className="flex items-center gap-3 pt-1 border-t border-white/5">
                  <div className="flex-1"><div className="text-sm font-semibold text-slate-100 flex items-center gap-1.5"><MapPin size={13} className="text-amber-300" /> Restrict to office IP only</div><p className="text-[11px] text-slate-400">Sign-in only from the office network set in Admin → Settings.</p></div>
                  <Toggle on={!!access.office_ip_only} onChange={(v) => setA("office_ip_only", v)} testid="perm-office-ip" />
                </div>
              )}
            </div>
          </section>
          <section className="space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><Globe2 size={12} /> Visibility</div>
            <div className="nxc-card p-3 space-y-4">
              {PERM_ROWS.map(([k, title, sub, opts]) => <div key={k}><div className="text-sm font-semibold text-slate-100">{title}</div><p className="text-[11px] text-slate-400 mb-2">{sub}</p><Seg value={perms[k]} options={opts} onChange={(v) => setP(k, v)} testid={`perm-${k}`} /></div>)}
              <div>
                <div className="text-sm font-semibold text-slate-100">Location filter</div><p className="text-[11px] text-slate-400 mb-2">Only see staff from one office (within the allowed departments)</p>
                <select className="nxc-input" value={perms.location_filter || ""} onChange={(e) => setP("location_filter", e.target.value)} data-testid="perm-location-filter">
                  <option value="">No filter — all locations</option>
                  {offices.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </section>
          <section className="space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Messaging</div>
            <div className="nxc-card p-3 divide-y divide-white/5">
              {TOGGLES.map(([k, title, sub]) => (
                <div key={k} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="flex-1"><div className="text-sm font-semibold text-slate-100">{title}</div><p className="text-[11px] text-slate-400">{sub}</p></div>
                  <Toggle on={!!perms[k]} onChange={(v) => setP(k, v)} testid={`perm-${k}`} />
                </div>
              ))}
            </div>
          </section>
          <p className="text-[11px] text-slate-500">Changes apply within seconds on the member's device. They are not notified.</p>
        </div>
      </div>
    </div>
  );
};
